// A tab left open keeps running the version it loaded, while translations and the API move on (a
// customer saw raw keys like "sub_active_title" from a days-old tab, 2026-10-07). Every few minutes,
// and when the tab comes back, this checks whether a newer build is live; if so, it reloads the next
// time the person changes screens or returns to the tab, never in the middle of a form.
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const CHECK_EVERY_MS = 10 * 60 * 1000;
const MAIN = /\/static\/js\/main\.[0-9a-f]+\.js/;

const loadedMain = () => {
  const s = [...document.scripts].map((x) => x.getAttribute("src") || "").find((src) => MAIN.test(src));
  return s ? s.match(MAIN)[0] : null;
};

async function liveMain() {
  try {
    const r = await fetch("/index.html", { cache: "no-store" });
    const m = (await r.text()).match(MAIN);
    return m ? m[0] : null;
  } catch {
    return null; // offline or a hiccup: try again later
  }
}

export default function useFreshVersion() {
  const stale = useRef(false);
  const location = useLocation();
  const first = useRef(true);

  useEffect(() => {
    const mine = loadedMain();
    if (!mine) return undefined; // the dev server: no hashed bundle
    const check = async () => {
      const live = await liveMain();
      if (live && live !== mine) stale.current = true;
    };
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (stale.current) window.location.reload();
      else check();
    };
    const timer = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  // Changing screens is a safe moment: nothing typed is lost.
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (stale.current) window.location.reload();
  }, [location.pathname]);
}
